import { createAsyncThunk } from '@reduxjs/toolkit';
import { isFunction } from 'lodash';

import type { Item } from '../../../models';
import { FuelPortalService } from '../../../services';
import { IngestService } from '../../../services/ingest-service';
import S3Multipart from '../../../services/s3-multipart';
import { SchemaType } from '../../forms/Asset-Editor/SchemaTypeOptions';
import type { RootState } from '..';
import { SetSnackbarMessage } from '../application-data';
import type { SaveAssetJob } from './SaveAssetJob';
import { removeJob, updateJob } from './SaveAssetJobsState';
import { toSaveAssetRequest } from './toSaveAssetRequest';
import { uploadFile } from './uploadFile';

const MULTIPART_LIMIT = 100000000; // 100 MB
const MULTIPART_CHUNK_SIZE = 100000000; // 100 MB

export const processSaveAssetJob = createAsyncThunk(
  'saveAssetJobs/processSaveAssetJob',
  async (job: SaveAssetJob, { dispatch, getState }) => {
    // console.log('processSaveAssetJob', { job });
    const state = getState() as RootState;
    const { categories } = state.applicationData;
    const { schemas } = state.applicationData;
    const { fuelUserAccessData } = state.fuelUserAccess;
    const { ouData } = state.applicationData;
    const isBatchUpdload = state.saveAssetJobs.batchUploadEnable;
    // console.log('processSaveAssetJob', { job, schemas, categories });
    const updateMessage = (message: string) => {
      // console.log('message', message);
      job.message = message;
      dispatch(updateJob(job));
    };

    job.progress = 0;
    dispatch(updateJob(job));

    const file = job.assetForm.ingestFile;
    // console.log('Showing Upload closed captions file link by default.');

    const asset = toSaveAssetRequest(job.assetForm, schemas, categories, fuelUserAccessData, ouData);

    updateMessage(`Creating asset...`);
    // multiple Item type signatures running around, i believe they are compatible though...
    // asset = await FuelPortalService.createAsset(asset as Item);
    const createAssetResult = await FuelPortalService.createNewAsset(asset as Item);
    if (!createAssetResult) {
      job.jobStatus = 'Failed';
      return false;
    }
    const { clip, s3SourcePath, multiPartUploadEndpoint, singlePartUploadUrl } = createAssetResult;

    const returnItemCallback = job?.extraParams?.returnItemCallback;
    if (returnItemCallback != null && isFunction(returnItemCallback)) {
      returnItemCallback(clip, job.index);
    }

    job.assetForm.originalAssetData = clip;
    job.jobStatus = 'InProgress';
    job.progress = 10;

    dispatch(updateJob(job));
    // aalt only show Upload closed captions link for "video" type assets.
    if (job && job.assetForm && job.assetForm.schemaType !== SchemaType.Video) {
      // console.log('Hiding Upload closed captions file link.');
      job.showSubtitleLink = false;
    } else {
      job.showSubtitleLink = true;
    }

    try {
      const fileSize = file.size;
      // console.log('[processSaveAssetJob] fileSize', fileSize);
      // #379161 Implementing AWS multipart upload support for UI
      // For File size bigger than 100MB, we will use multi-part upload with chunk size of 100MB
      // fileSize     dEMO1.MXF                          25GB ~ 25,769,304,636
      // fileSize     CRIBBS070121.mxf                8.078GB ~  8,272,202,132
      // fileSize     storybots_planets.mp4             207MB ~    212,891,059
      // QA_BitC_2021_02_08_Sample_HOUSE_EXPLOSION.mp4   77MB ~     79,333,568
      // 100MB = 1e+8 = 100,000,000

      if (fileSize > MULTIPART_LIMIT) {
        updateMessage(`Preparing for multi-part upload...`);
        job.progress = 20;
        dispatch(updateJob(job));

        const numParts = Math.ceil(file.size / MULTIPART_CHUNK_SIZE);
        let part1Url = null;
        let sourceFileUrl = null;
        let isMultipartUploadComplete = false;

        const s3multipart = new S3Multipart({
          parallelism: 1,
          partSize: MULTIPART_CHUNK_SIZE,
          retryAttempts: 3,
          createUpload: (file) => {
            return new Promise(async (resolve, reject) => {
              const createUploadData = await IngestService.getMultipartUploadUrl(multiPartUploadEndpoint, { numParts });

              // sample response
              // {
              //   "sourceFileUrl": "s3://fueltools-dev01-sources/fe5d4764-87e0-49d1-a171-a4e56ec36d06/20211116/aeff65e0-6b29-4cd6-9b0a-afce06be73ed/storybots_planets.mp4",
              //   "s3UploadUrl": "https://fueltools-dev01-sources.s3.amazonaws.com/fe5d4764-87e0-49d1-a171-a4e56ec36d06/20211116/aeff65e0-6b29-4cd6-9b0a-afce06be73ed/storybots_planets.mp4?uploadId=...&partNumber=0&X-Amz-Expires=300&X-Amz-Security-Token=...",
              //   "multiPartSourceUploadId": "HeFuoOuy9JVDaYwkh5Py6bXNdi5sFAg...",
              //   "externalSubtitleFileUrl": "...",
              //   "externalSubtitleS3UploadUrl": "...",
              //   "externalThumbnailFileUrl": "...",
              //   "externalThumbnailS3UploadUrl": "..."
              //  }

              // we get the first part upload url from the response above
              sourceFileUrl = createUploadData.sourceFileUrl;
              part1Url = createUploadData.s3UploadUrl;
              resolve(createUploadData.multiPartSourceUploadId); // supposed to simply return the aws multipart upload id
            });
          },
          getPartUrl: (file, uploadId, partNumber, partSize) => {
            return new Promise(async (resolve, reject) => {
              if (uploadId == null) {
                reject();
              }
              if (partNumber === 1) {
                resolve(part1Url);
                return;
              }
              const getPartUrlData = await IngestService.getMultipartUploadUrl(multiPartUploadEndpoint, { uploadId, partNumber });
              resolve(getPartUrlData.s3UploadUrl); // supposed to return the upload url for this part only
            });
          },
          completeUpload: (file, uploadId, etags) => {
            return new Promise(async (resolve, reject) => {
              if (uploadId == null) {
                reject();
              }
              const completeRequestData = {};
              let eTagIndex = 0;
              for (let i = 0; i < etags.length; i++) {
                const element = etags[i];
                if (element != null) {
                  eTagIndex++;
                  const eTag = element.replace(/"/g, ''); // remove double quotes if present
                  completeRequestData[eTagIndex] = eTag;
                }
              }

              if (eTagIndex !== numParts) {
                isMultipartUploadComplete = false;
                reject(`ETags count ${eTagIndex} do not match numParts ${numParts}`);
              }

              const completeUploadData = await IngestService.getMultipartUploadUrl(
                multiPartUploadEndpoint,
                { uploadId },
                completeRequestData
              );
              isMultipartUploadComplete = true;
              resolve(null); // return nothing to indicate success
            });
          },
          onProgress: (uploadedBytes, totalBytes) => {
            const progress = Math.round((uploadedBytes / totalBytes) * 100); // progress is a number between 0 and 100, eg. 10, 28, 70, 94, 100
            job.progress = progress;
            dispatch(updateJob(job));
          },
        });

        await s3multipart.upload(file).then(async () => {
          if (!isMultipartUploadComplete) {
            return;
          }
          updateMessage(`Starting ingest...`);
          await FuelPortalService.createNewIngest(createAssetResult);

          updateMessage(`Asset successfully created.`);
        });
      } else {
        updateMessage(`Uploading file...`);
        await uploadFile(file, singlePartUploadUrl, (progress) => {
          // progress is a number between 0 and 100, eg. 10, 28, 70, 94, 100
          job.progress = progress;
        });
        updateMessage(`Starting ingest...`);
        await FuelPortalService.createNewIngest(createAssetResult);
        updateMessage(`Asset successfully created.`);
      }
    } catch (err) {
      console.error(err);
      updateMessage(`There was an error creating the asset.`);
      await IngestService.deleteClip(clip.id);
      throw err;
    }

    clearInterval(job.intervalId);
    job.progress = 100;
    job.jobStatus = 'Completed';
    dispatch(updateJob(job));
    dispatch(removeJob(job));
    if (!isBatchUpdload) {
      dispatch(SetSnackbarMessage({ message: 'Asset successfully created.', severity: 'success' }));
    }
    return true;
  }
);
